<template>
  <div
    :id="props.id"
    :class="{ isopen: props.isopen, printview: props.isprintView }"
    :style="taskStyle"
    class="task"
    ref="taskEl"
    @click="handleClick"
    @dblclick="handleDoubleClick"
  >

<div v-if="props.isproject" :class="props.isprintView ? 'day-container-pdf' : 'day-container'">
  <div v-for="d in dayCells" :key="d.index"
       :class="props.isprintView ? 'day-cell-pdf' : 'day-cell' + (props.project.id === 0 ? ' available' : '')"
       :style="{ width: widthPerDay + 'px', minWidth: widthPerDay + 'px' }">
    {{ d.label > 0 ? d.label : "" }}
  </div>
</div>

<template v-if="!props.isproject && ((props.isprintView && (booking.project_id === props.project.id)) || !props.isprintView)">

      <div v-if="weekStartCells.length === 0"
       class="content" :style="{marginLeft: '5px', color: 'gray'}">
      {{ props.isprintView ? "" : props.label}}
      </div>
      <div
        v-for="d in weekStartCells"
        class="day-cell"
        :style="{ position: 'absolute', left: d.index * widthPerDay + 'px', marginLeft: '5px', color: 'gray' }"
      >
      {{ props.isprintView ? "" : props.label}}
      </div>
    </template>

    <div class="resize-handle left" @pointerdown="handleResize"></div>
    <div class="resize-handle right" @pointerdown="handleResize"></div>
    <div class="drag-handle" @pointerdown="handleDrag"></div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch, inject } from 'vue';
import {
  formatDateYYYYMMDD,
  getWidthPerDay,
  rowHeight,
  dayMs,
  snap
} from "./planner.js";
import { POPUP_BUS } from './popupBus';
import * as d3 from 'd3';

import {transparent} from '@/utils/globals'
import {cleanx, handleAutoScroll, isoDateToUTC, isWeekstart} from "./planner.js";

import {useViewStore} from "@/stores/viewStore"

const props = defineProps({
  id: { type: String, required: true},
  isproject: { type: Boolean, required: true},
  project: { type: Object, required: true},
  isprintView: {type: Boolean, required: true},
  user:  { type: Object},
  booking: { type: Object},
  bookings: { type: Array},
  label: { type: String},
  start: { type: Date, required: true},
  end: { type: Date, required: true},
  startDate: { type: Date, required: false},
  endDate: { type: Date, required: false},
  inproject: { type: Boolean, required: true},
  configs: { type: Array, required: true },
  isopen: { type: Boolean },
});

const emit = defineEmits(['bookingDone', 'taskChange', 'cancel', 'doubleClick']);

const widthPerDay = computed(() => {
  return getWidthPerDay(props.isprintView)
})

const activeStartDate = computed(() => {
  if (props.startDate) return props.startDate;
  const config = props.configs.find(c => c.name === "startdate");
  return new Date(config.value + "T00:00:00");
});

const activeEndDate = computed(() => {
  if (props.endDate) return props.endDate;
  const config = props.configs.find(c => c.name === "enddate");
  return new Date(config.value + "T00:00:00");
});

// 3. Make the D3 Scale reactive
// By using a computed property for 'x', every style calculation 
// depending on x() will automatically update when the dates shift.
const x = computed(() => {
  const totalDays = (activeEndDate.value - activeStartDate.value) / dayMs;
  //console.log("totalDays=" + totalDays)
  //console.log("activeStartDate=" + activeStartDate.value)
  //console.log("activeEndDate=" + activeEndDate.value)
    return d3.scaleUtc()
    .domain([activeStartDate.value, activeEndDate.value])
    .range([0, totalDays * widthPerDay.value]);
});

// Local state
const currentStart = ref(props.start);
const currentEnd   = ref(props.end);

const task_left_offset = 5;

watch(() => props.start, newStart => currentStart.value = newStart);
watch(() => props.end,   newEnd   => currentEnd.value   = newEnd);

const view = useViewStore();

function countBookingsForDay(bookings, d) {
	let count = bookings.filter(b => d >= new Date(b.start) && d <= new Date(b.end)).length
	if (props.project.id === 0) {
		count = view.countActiveUsers - count
	}
	return count
}

function getBookings() {
	if (!props.bookings || props.bookings.length === 0) {
		return []
	}
	
	if (props.project.id === 0) {
		return props.bookings
	}
	
	return props.bookings.filter(b => b.project_id === props.project.id)	
}

function getUserBookings() {
	if (!props.bookings || props.bookings.length === 0) {
		return []
	}	
	return props.bookings.filter(b => b.user_id === props.user.id)	
}

const pBookings = computed(() => getBookings())
const uBookings = computed(() => props.user ? getUserBookings() : [])

const dayCells = computed(() => {
	  const cells = [];
		
		let d = new Date(currentStart.value);
		let i = 0;
				 
		while (d <= currentEnd.value) {
		  let count = countBookingsForDay(pBookings.value, d);
		  d = new Date(d.getTime() + dayMs);
		  cells.push({
		    index: i,
        day: d,
		    label: count,
		  });
		  i++;
		}		
	  return cells;
});
	
const weekStartCells = computed(() => {
	  const cells = [];
		
		let d = new Date(currentStart.value);
		let i = 0;
				 
		while (d <= currentEnd.value) {
      if (isWeekstart(d)) {
		  cells.push({
		    index: i,
        day: d,
		  });
      }
      d = new Date(d.getTime() + dayMs);
		  i++;
		}		
	  return cells;
});

function projectColor(props) {
  if (props.isprintView) {
    return 'lightgray'
  }
  return props.inproject ? transparent(props.project.color, 0.5) : 'transparent'
}

function projectBorderColor(props) {
  if (props.isprintView) {
    return '1px solid black'
  }
  return props.inproject ? props.project.color : '1px solid gray'
}

const taskStyle = computed(() => {
  // Use .value because x is now a computed ref
  const leftPx = cleanx(x.value, currentStart.value);
  const rightPx = cleanx(x.value, currentEnd.value);
  const width = rightPx - leftPx + widthPerDay.value;

  return {
    top: `${3 + (props.isprintView ? 5 : 0)}px`,
    left: `${leftPx - (props.isproject ? 5 : 0)}px`,
    width: `${width - 5}px`,
    height: `${rowHeight - (props.isprintView ? 6 : 0) - 12}px`,
    color: 'black',
    backgroundColor: projectColor(props),
    border: projectBorderColor(props)
  };
});

const getId = () => props.isproject ? props.project.id : props.booking.id;

function updateBooking(cstart, cend, shift, changedir) {
  const params = {
    isproject: props.isproject,
    id: getId(),
    booking_action: props.project.id,
    selected_pid: props.project.id, 
    selected_bid: props.booking ? props.booking.id : null,
    firstDay: cstart,
    lastDay: cend,
    selectedResources: props.user ? [props.user.id] : [],
    shift: shift,
    changedir: changedir,
  };
  emit('bookingDone', params);
}

function intersectingBooking(b, ustart, uend) {
	if (b.id === props.booking.id) {
		return false
	}
	console.log("intersectingBooking ustart=" + ustart + " uend=" + uend)
	console.log("intersectingBooking b.start=" + b.start + " b.end=" + b.end)
	console.log("intersectingBooking b.oldstart=" + isoDateToUTC(b.oldstart) + " b.oldend=" + isoDateToUTC(b.oldend))
	
	if (isoDateToUTC(b.oldstart) > uend) {
		return false
	}
	
	if (isoDateToUTC(b.oldend) < ustart) {
		return false;
	}
	
	return true
}

function updateTask(taskDiv, initialWidth, initialLeft, cstart, cend, shift, changedir) {

  let ustart = isoDateToUTC(cstart)
  let uend = isoDateToUTC(cend)
  console.log("cstart=" + cstart + " cend=" + cend + " ustart=" + ustart + " uend=" + uend)
  let intersectingBookings = uBookings.value.filter(b => intersectingBooking(b, ustart, uend))
  console.log("intersectingBookings=" + JSON.stringify(intersectingBookings))
  if (intersectingBookings.length > 0) {
	  if (intersectingBookings.find(b => b.project_id === 0)) {
		window.alert("Umbunchungen vom Urlaubbuchungen nicht möglich. Bitte zuerst frei machen.")
		setTaskDim(taskDiv, initialWidth, initialLeft)
		return
	  }
	  if (!window.confirm("Überschneidet andere Buchung. Möchten Sie wirklich")) {
		setTaskDim(taskDiv, initialWidth, initialLeft)
		return
	  }
  }

  currentStart.value = cstart;
  currentEnd.value = cend;
  updateBooking(cstart, cend, shift, changedir);
}

function getTask(e) {
  return e.target.closest('.task');
}

const bus = inject(POPUP_BUS);
let clickTimeout = null;

function handleClick(e) {
  bus.closePopup();

  if (clickTimeout) clearTimeout(clickTimeout);

  if (props.isprintView) {
    return
  }

  clickTimeout = setTimeout(() => {
    clickTimeout = null;

    if (window.getSelection) {
      const sel = window.getSelection();
      if (sel) sel.removeAllRanges();
    }

    document.querySelectorAll(".task.editable").forEach(t => t.classList.remove("editable"));
    const taskDiv = getTask(e);
    taskDiv.classList.add("editable");
    let content = taskDiv.querySelector('.content');
    if (content) {
      content.style.display = "none";
    }
    
  }, 200);
}

function handleDoubleClick(e) {
  if (clickTimeout) {
    clearTimeout(clickTimeout);
    clickTimeout = null;
  }

  if (props.isprintView) {
    return
  }

  document.querySelectorAll(".editable").forEach(t => t.classList.remove("editable"));
  document.querySelectorAll(".highlight-bar").forEach(t => t.classList.remove("highlight-bar"));

  const taskDiv = getTask(e);
  taskDiv.classList.add("highlight-bar");

  bus.openPopupAt(e.currentTarget, {
    id: getId(),
    html_id: props.id,
    uid: props.isproject ? null : props.user.id,
    start: currentStart.value,
    end: currentEnd.value,
    isproject: props.isproject
  });
}

function setTaskDim(taskDiv, width, left) {
  taskDiv.style.left = `${left}px`;
  taskDiv.style.width = `${width}px`;	
}

function handleDrag(e) {
  e.preventDefault();
  e.stopPropagation();

  let dragHandle = e.target;
  let taskDiv = getTask(e);

  const container = document.querySelector('.main-scroll-area');

  const startScrollLeft = container.scrollLeft;
  const resizeStartX = e.clientX;
  const initialLeft = parseFloat(taskDiv.style.left);
  const initialWidth = parseFloat(taskDiv.style.width);

  let cstart = null;
  let cend   = null;

  dragHandle.setPointerCapture(e.pointerId);

  let dx = 0;
  const onPointerMove = e2 => {
    handleAutoScroll(e2);

    const scrollDx = container.scrollLeft - startScrollLeft;
    dx = snap((e2.clientX - resizeStartX) + scrollDx);

    let newLeft = snap(initialLeft + dx);
    newLeft = Math.max(0, newLeft);
    taskDiv.style.left = `${newLeft}px`;

    cstart = formatDateYYYYMMDD(x.value.invert(newLeft + widthPerDay.value));
    cend   = formatDateYYYYMMDD(x.value.invert(newLeft + initialWidth));

    emit('taskChange', cstart, cend);
  };

  const onPointerUp = () => {
    updateTask(taskDiv, initialWidth, initialLeft, cstart, cend, true, dx > 0);
    dragHandle.removeEventListener("pointermove", onPointerMove);
    dragHandle.removeEventListener("pointerup", onPointerUp);
  };

  dragHandle.addEventListener("pointermove", onPointerMove);
  dragHandle.addEventListener("pointerup", onPointerUp);
}

function handleResize(e) {
  e.preventDefault();
  e.stopPropagation();

  const taskDiv = getTask(e);
  const initialWidth = Math.round(parseFloat(taskDiv.style.width));
  const initialLeft  = Math.round(parseFloat(taskDiv.style.left));
  const isLeft = e.target.closest('.resize-handle').classList.contains('left');

  const container = document.querySelector('.main-scroll-area');
  const startScrollLeft = container.scrollLeft;
  const resizeStartX = e.clientX;

  taskDiv.setPointerCapture(e.pointerId);
  let newLeft  = null;
  let newWidth = null;
  let cstart = null;
  let cend   = null;

  let dx = 0;
  const onPointerMove = (e2) => {
    handleAutoScroll(e2);
    const scrollDx = container.scrollLeft - startScrollLeft;
    dx = snap((e2.clientX - resizeStartX) + scrollDx);
    if (dx === 0) return;

    if (isLeft) {
      newLeft = snap(initialLeft + dx);
      newWidth = snap(initialWidth - dx);
    } else {
      newLeft = snap(initialLeft);
      newWidth = snap(initialWidth + dx);
    }

	if (newWidth > 20) {
	   setTaskDim(taskDiv, newWidth, newLeft)
	}

    cstart = formatDateYYYYMMDD(x.value.invert(newLeft + widthPerDay.value));
    cend   = formatDateYYYYMMDD(x.value.invert(newLeft + newWidth));
    emit('taskChange', cstart, cend);
  };

  const onPointerUp = () => {
    if (dx === 0) return;

    updateTask(taskDiv, initialWidth, initialLeft, cstart, cend, false, (isLeft && dx > 0) || (!isLeft && dx < 0));

    taskDiv.removeEventListener("pointermove", onPointerMove);
    taskDiv.removeEventListener("pointerup", onPointerUp);
  };

  taskDiv.addEventListener("pointermove", onPointerMove);
  taskDiv.addEventListener("pointerup", onPointerUp);
}

onMounted(() => {
  const taskDiv = document.getElementById(props.id);
  if (!taskDiv) return;

  const dragHandle = taskDiv.querySelector('.drag-handle');
  const content = taskDiv.querySelector('.content');
  const resizeHandles = taskDiv.querySelectorAll('.resize-handle');

  const observer = new MutationObserver(() => {
    let display = taskDiv.classList.contains("editable") ? "block" : "none";
    let notdisplay = taskDiv.classList.contains("editable") ? "none" : "block";

    if (!dragHandle) return;
    dragHandle.style.display = display;
    if (content) content.style.display = notdisplay;
    resizeHandles.forEach(handle => handle.style.display = display);
  });

  observer.observe(taskDiv, { attributes: true, attributeFilter: ["class"] });
});
</script>

<style scoped>
.task {
  overflow: hidden;
  position: absolute;
  border-radius: 4px;
  color: white;
  font-size: 10px;
  display: flex;
  align-items: center;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  z-index: 2;
}


.task.isopen {
  top: 0;
  height: 20px !important;
}

.task:active {
  opacity: 0.8;
  cursor: grabbing;
}

/* ✔ Day cells */
.day-container {
  display: flex;
  height: 100%;
  width: 100%;
  pointer-events: none; /* Prevent blocking drag/resize */
}

.day-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: gray;
  user-select: none;
}

.day-cell.available {
  color: rgb(240, 255, 240);
}

.resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 30px;
  margin: 0 -10px;
  z-index: 2;
  background: transparent;
  cursor: ew-resize;
  display: none;
  touch-action: none;
}

.resize-handle::before {
  content: "";
  position: absolute;
  top: 25%;
  bottom: 25%;
  width: 10px;
  background-color: #666;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 2px;
}

.resize-handle.left { left: 5px; }
.resize-handle.right { right: 5px; }

.drag-handle {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  background-color: #444;
  transform: translate(-50%, -50%);
  border-radius: 4px;
  cursor: grab;
  z-index: 10;
  touch-action: none;
}

.drag-handle:active {
  cursor: grabbing;
}

.printview {
  touch-action: none !important;
  pointer-events: none !important;
  user-select: none !important;
}

.day-container-pdf {
  display: inline-block;
  white-space: nowrap; /* horizontal layout */
  height: 30px;
}

.day-cell-pdf {
  display: inline-block;
  text-align: center;
  vertical-align: middle;
  height: 100%;
}


</style>
